'use client';
import { TZDate } from '@date-fns/tz';
import { startOfDay } from 'date-fns';
import { useReducer } from 'react';
import { useGetApiMatchesMatchesCalendar } from '@/api/backoffice/matches/matches';
import Calendar from '@/components/calendar';
import { MatchListItem } from '@/components/cards/MatchListItem';
import { Pagination } from '@/components/pagination';
import SkeletonEvent from '@/components/skeletons/event';
import { useGetGameFromRouteParams } from '@/hooks/useGetGameFromParams.hook';
import { cn } from '@/utils/cn';
import { mapGameFilterToVideoGameName } from '@/utils/gameMappers';

enum FilterType {
  Live = 'running',
  Upcoming = 'not_started',
  Finished = 'finished',
}

type MatchesState = {
  filter: FilterType;
  date: TZDate;
  selectedPage: number;
  isLoading: boolean;
};

type MatchesAction =
  | { type: 'SET_FILTER'; payload: FilterType }
  | { type: 'SET_DATE'; payload: TZDate }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean };

function matchesReducer(
  state: MatchesState,
  action: MatchesAction
): MatchesState {
  switch (action.type) {
    case 'SET_FILTER':
      return {
        ...state,
        filter: action.payload,
        selectedPage: 1,
      };
    case 'SET_DATE':
      return {
        ...state,
        date: action.payload,
        selectedPage: 1,
      };
    case 'SET_PAGE':
      return {
        ...state,
        selectedPage: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
}

export const MatchesList = () => {
  const { game } = useGetGameFromRouteParams();

  const [state, dispatch] = useReducer(matchesReducer, {
    filter: FilterType.Live,
    date: startOfDay(new TZDate()),
    selectedPage: 1,
    isLoading: false,
  });

  // Map the game filter to API parameter
  const gameParam = mapGameFilterToVideoGameName(game);

  // Format the date properly for the API - ISO string format
  const formattedDate = state.date.toISOString().split('T')[0];

  // Construct API parameters
  const queryParams = {
    Status: state.filter,
    BeginAt: formattedDate,
    Game: gameParam,
    PageNumber: state.selectedPage,
    PageSize: 10,
  };

  // Use the generated hook
  const { data, isLoading } = useGetApiMatchesMatchesCalendar(queryParams);

  // Get array of enum values for iteration
  const filters = Object.values(FilterType);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-lg bg-medium-gray p-4">
        <p className="font-medium text-blue-gray text-sm leading-5">
          Match filters
        </p>
        <div className="flex flex-row gap-2">
          {filters.map((filterValue) => {
            // Get the enum key (Live, Upcoming, Finished) from the value
            const filterKey = Object.keys(FilterType).find(
              (key) =>
                FilterType[key as keyof typeof FilterType] === filterValue
            );

            return (
              <button
                aria-label={`Filter by ${filterKey?.toLowerCase()} matches`}
                aria-pressed={state.filter === filterValue}
                className={cn(
                  'flex items-center justify-center gap-1 rounded-lg px-2 py-2 md:min-w-[140px]',
                  state.filter === filterValue &&
                    'border border-light-blue bg-light-blue-o10'
                )}
                key={filterValue}
                onClick={() => {
                  dispatch({
                    type: 'SET_FILTER',
                    payload: filterValue,
                  });
                }}
                type="button"
              >
                {filterKey}
              </button>
            );
          })}
        </div>
        {state.filter !== FilterType.Live && (
          <Calendar
            onChange={(newDate: Date) => {
              dispatch({
                type: 'SET_DATE',
                payload: startOfDay(new TZDate(newDate)),
              });
            }}
            type={state.filter === FilterType.Finished ? 'past' : 'future'}
          />
        )}
      </div>
      <ul className="space-y-1 rounded-lg bg-dark-gray p-4">
        {isLoading &&
          Array.from({ length: 10 }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rendering skeleton
            <SkeletonEvent key={`skeleton-${index}`} />
          ))}

        {!isLoading && data?.data.items ? (
          data.data.items.length === 0 ? (
            <div className="flex flex-col gap-1 rounded-lg bg-card-bg p-3 hover:bg-light-blue-o15">
              <p className="font-medium text-sm leading-[18px]">
                No games found
              </p>
              <p className="font-normal text-[10px] leading-3">
                Try again later
              </p>
            </div>
          ) : (
            data.data.items.map((item) => (
              <MatchListItem data={item} key={item.id} />
            ))
          )
        ) : (
          <div className="flex flex-col gap-1 rounded-lg bg-card-bg p-3 hover:bg-light-blue-o15">
            <p className="font-medium text-sm leading-[18px]">
              No data available
            </p>
          </div>
        )}
      </ul>
      {!isLoading && data && (
        <Pagination
          currentPage={state.selectedPage}
          onChange={(page) => dispatch({ type: 'SET_PAGE', payload: page })}
          perPageItems={queryParams.PageSize}
          totalCount={data.data.totalCount || 0}
          totalPages={data.data.totalPages || 1}
        />
      )}
    </div>
  );
};
